import { createFileRoute, Link } from "@tanstack/react-router";
import { PolicyLayout } from "@/components/PolicyLayout";

export const TRUCOINS_TERMS_VERSION = "1.2";
export const TRUCOINS_TERMS_UPDATED = "4 October 2026";

export const Route = createFileRoute("/policies/trucoins-terms")({
  component: TruCoinsTerms,
  head: () => ({
    meta: [
      { title: "TruCoins Loyalty & Refer and Earn Terms. TruWe" },
      {
        name: "description",
        content:
          "Plain-English terms for TruWe's TruCoins loyalty programme and Refer & Earn: how you earn coins, coin value, redemption limits, expiry, refunds and referral rules.",
      },
      { name: "robots", content: "index, follow" },
      { name: "twitter:card", content: "summary" },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "TruCoins Loyalty & Refer and Earn Terms. TruWe" },
      {
        property: "og:description",
        content:
          "How TruCoins are earned, valued, redeemed and reversed on TruWe, plus the full Refer & Earn rules.",
      },
      { property: "og:url", content: "https://truwe.in/policies/trucoins-terms" },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/policies/trucoins-terms" }],
  }),
});

type Section = { id: string; title: string };

const SECTIONS: Section[] = [
  { id: "overview", title: "1. Overview and nature of TruCoins" },
  { id: "eligibility", title: "2. Eligibility and one-account rule" },
  { id: "value", title: "3. Value of a TruCoin" },
  { id: "earning", title: "4. Earning TruCoins" },
  { id: "calculation", title: "5. How earnings are calculated" },
  { id: "availability", title: "6. Pending, availability and cancellations" },
  { id: "redemption", title: "7. Redeeming TruCoins" },
  { id: "tax", title: "8. Tax, invoicing and non-eligible charges" },
  { id: "expiry", title: "9. Expiry" },
  { id: "refunds", title: "10. Refunds, reversals and returns" },
  { id: "founding", title: "11. Founding 99 earn rate" },
  { id: "refer-earn", title: "12. Refer & Earn" },
  { id: "referral-limits", title: "13. Referral stacking, abuse and reversal" },
  { id: "fraud", title: "14. Fair investigation and fraud process" },
  { id: "changes", title: "15. Programme changes and closure" },
  { id: "privacy", title: "16. Privacy and communications" },
  { id: "grievance", title: "17. Grievance, governing law and consumer rights" },
];

function TruCoinsTerms() {
  return (
    <PolicyLayout
      title="TruCoins Loyalty Program & Refer & Earn Terms"
      updated={TRUCOINS_TERMS_UPDATED}
    >

      <nav aria-label="On this page" className="mt-6 rounded-2xl border border-border bg-card/60 p-4">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          On this page
        </p>
        <ol className="grid gap-1 text-sm md:grid-cols-2">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="text-muted-foreground hover:text-brand">
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="rounded-2xl border border-brand/20 bg-brand-soft/40 p-5">
        <p className="font-display text-sm font-semibold text-brand-deep">
          New to TruCoins?
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Read the friendly guide to earning, redeeming and sharing TruCoins.
        </p>
        <Link
          to="/trucoins"
          className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
        >
          How TruCoins work
          <span aria-hidden>→</span>
        </Link>
      </div>

      <p>
        These TruCoins Loyalty Program and Refer &amp; Earn Terms (&ldquo;Programme
        Terms&rdquo;) apply in addition to our{" "}
        <Link to="/policies/terms-of-service">Terms of Service</Link>,{" "}
        <Link to="/policies/privacy-policy">Privacy Policy</Link>,{" "}
        <Link to="/policies/shipping-policy">Shipping Policy</Link>,{" "}
        <Link to="/policies/return-exchange-policy">Return &amp; Exchange Policy</Link> and{" "}
        <Link to="/policies/refund-policy">Refund Policy</Link>. If there is any conflict on a
        programme-specific point, these Programme Terms control that point only.
      </p>


      <h2 id="overview">1. Overview and nature of TruCoins</h2>
      <p>
        TruCoins are a <strong>merchant-funded promotional discount / loyalty credit</strong>
        offered by TruWe to eligible customers as a thank-you for shopping with us. When
        redeemed, TruCoins appear on the tax invoice as a discount applied to the pre-GST
        merchandise value of eligible TruWe products, in line with normal promotional-discount
        practice. TruCoins are <strong>not</strong> money, a stored-value balance, a wallet, a
        bank balance, a payment account, a prepaid instrument, a transferable asset, a
        top-up or reload facility, or a payment method. TruCoins cannot be purchased, topped
        up, transferred, sold, gifted, withdrawn as cash, exchanged for cash, converted to
        any other currency or credit, or used by any third party or on any non-TruWe
        website or store, and do not earn interest.
      </p>

      <h2 id="eligibility">2. Eligibility and one-account rule</h2>
      <p>
        Only verified TruWe customer accounts in good standing are eligible. One account per
        person, one per phone number, and one per email address. We may exclude duplicate
        accounts, resale, bulk-buying misuse, or activity we reasonably believe is fraudulent
        or in bad faith.
      </p>

      <h2 id="value">3. Value of a TruCoin</h2>
      <p>
        Each TruCoin gives a promotional discount value of <strong>₹0.50</strong> (fifty
        paise) off the pre-GST merchandise value of eligible TruWe products, subject to the
        caps and eligibility rules in these Programme Terms. For example,{" "}
        <strong>20 TruCoins = ₹10 discount</strong> and{" "}
        <strong>500 TruCoins = ₹250 discount</strong>. TruCoins are not cash, are not
        withdrawable, are not transferable, are not a wallet balance, and are not a payment
        instrument. GST is calculated on the reduced (net-of-discount) taxable merchandise
        value. Shipping charges, shipping tax, COD/handling fees and other non-merchandise
        charges are <strong>not</strong> discounted by TruCoins and must be paid in full.
      </p>


      <h2 id="earning">4. Earning TruCoins</h2>
      <ul>
        <li>Earnings apply only to <strong>fully prepaid</strong> orders.</li>
        <li>Standard earn rate: <strong>5%</strong> of the eligible base.</li>
        <li>Founding 99 members: <strong>7%</strong> of the eligible base.</li>
        <li>No TruCoins are earned or may be redeemed on Cash on Delivery (COD) orders.</li>
        <li>
          <strong>Reviews:</strong> 50 TruCoins are credited for every review published on a
          product you have purchased. The reward is identical for every star rating, a 1-star
          review earns exactly the same as a 5-star review. Reviews are invite-only after
          delivery and are removed only for spam, abuse, off-topic content or content we cannot
          match to a genuine order; we never remove or edit a review for being critical. This
          incentive is disclosed alongside the reviews on our product pages.
        </li>
      </ul>


      <h2 id="calculation">5. How earnings are calculated</h2>
      <p>
        Earnings are calculated on the eligible <strong>ex-GST product value</strong> after
        applicable discounts and <strong>excluding</strong> GST, shipping, other fees,
        coupons, gift cards, and any portion of the order paid with TruCoins. Earnings are
        rounded <strong>down</strong> to whole TruCoins. A maximum of <strong>500 TruCoins</strong>{" "}
        may be earned per order.
      </p>

      <h2 id="availability">6. Pending, availability and cancellations</h2>
      <p>
        Earned TruCoins are marked <em>pending</em> from order placement until delivery, and
        become <em>available</em> for redemption <strong>7 days after delivery</strong>. Coins
        are not earned (or are reversed) if the order is cancelled, returned, refunded, marked
        Return-To-Origin (RTO), fails delivery, or is found to be fraudulent.
      </p>

      <h2 id="redemption">7. Redeeming TruCoins</h2>
      <ul>
        <li>Redemption is available only on <strong>prepaid</strong> orders.</li>
        <li>Minimum redemption: <strong>20 TruCoins = ₹10 discount</strong>.</li>
        <li>Minimum eligible ex-GST merchandise value: <strong>₹999</strong>.</li>
        <li>
          Maximum redemption on an order is the <strong>lower</strong> of: your available
          balance, <strong>500 TruCoins = ₹250 discount</strong>, and{" "}
          <strong>20% of the eligible ex-GST merchandise value</strong>.
        </li>
      </ul>

      <h2 id="tax">8. Tax, invoicing and non-eligible charges</h2>
      <p>
        TruCoins cannot be used to pay GST, shipping, tax on shipping, COD or handling fees,
        or any non-merchandise charges. Displayed prices may include GST. Your cart and tax
        invoice will show the taxable value, applicable tax, TruCoins applied, shipping and
        any fees, and the final payable amount. The final tax invoice and applicable law
        govern the tax treatment of the transaction. We do not promise a fixed 5% GST rate
        for all SKUs, the actual rate for each SKU is the rate applicable under law at the
        time of invoicing.
      </p>

      <h2 id="expiry">9. Expiry</h2>
      <p>
        Available TruCoins expire <strong>180 days</strong> after the date on which they
        became available. Expiry dates are shown in your account. Reminders, when sent, are a
        courtesy and are not a condition of expiry.
      </p>

      <h2 id="refunds">10. Refunds, reversals and returns</h2>
      <p>
        Reversal and reinstatement of TruCoins follow the underlying refund on a{" "}
        <strong>proportional</strong> basis. If redeemed TruCoins are reinstated to your
        account following a refund, they retain their <strong>original expiry date</strong>{" "}
        and are not extended. Please read our{" "}
        <Link to="/policies/refund-policy">Refund Policy</Link>,{" "}
        <Link to="/policies/return-exchange-policy">Return &amp; Exchange Policy</Link> and{" "}
        <Link to="/policies/shipping-policy">Shipping Policy</Link>.
      </p>

      <h2 id="founding">11. Founding 99 earn rate</h2>
      <p>
        The 7% Founding 99 earn rate is personal to the Founding 99 member, is
        non-transferable, and remains subject to continued programme and account eligibility.
      </p>

      <h2 id="refer-earn">12. Refer &amp; Earn (Beta)</h2>
      <p><strong>Refer &amp; Earn is disabled from 21 October 2026.</strong> No new referral signup bonuses, friend discounts or referral rewards are issued while disabled. TruCoins earning and redemption remain available, and previously earned balances are preserved. The rules below describe the programme when active.</p>
      <p>
        The Refer &amp; Earn programme is currently offered in <strong>beta</strong>. All
        rewards, thresholds, slabs and mechanics described below are indicative, may be
        modified, paused, or withdrawn at any time at our sole discretion, and confer no
        vested or contractual entitlement. Rewards are credited only after eligibility,
        anti-abuse and delivery checks are completed.
      </p>
      <ul>
        <li>
          The <strong>referred customer</strong> may receive <strong>₹100 off</strong> their
          first eligible prepaid order of at least <strong>₹1,000 ex-GST</strong>, plus a
          one-time <strong>200 TruCoins (₹100 value)</strong> signup credit added to their
          TruCoins balance for use on a future eligible order, subject to programme rules
          and anti-abuse checks.
        </li>
        <li>
          The <strong>referrer</strong> may receive a flat{" "}
          <strong>200 TruCoins (₹100 value)</strong> per qualified referral, credited only
          after the referred order is delivered <strong>plus 7 days</strong>. Reward values
          are indicative during beta and may change without prior notice.
        </li>
        <li>Referral attribution lasts <strong>30 days</strong> from the referral action.</li>
        <li>
          Maximum <strong>five qualified referrals</strong> per referrer in any rolling
          30-day window.
        </li>
        <li>
          TruCoins have <strong>no cash value</strong>, are not redeemable for cash, cannot
          be transferred, and may be adjusted, expired or reversed in line with these
          Programme Terms.
        </li>
      </ul>

      <h2 id="referral-limits">13. Referral stacking, abuse and reversal</h2>
      <p>
        The referral benefit cannot be stacked with other first-order offers. Where more than
        one offer would apply, only the <strong>larger eligible benefit</strong> is used. No
        self-referral, circular referral, shared-identity referral, duplicate accounts,
        resale-driven referrals or other manipulation is permitted. Referral benefits will be
        reversed for orders that are cancelled, refunded, returned, marked RTO, or found to
        be fraudulent.
      </p>

      <h2 id="fraud">14. Fair investigation and fraud process</h2>
      <p>
        Where we have reasonable evidence of misuse, fraud, or breach of these Programme
        Terms, we may investigate and, in a proportionate way, temporarily suspend
        programme access, correct account balances, or reverse the specific TruCoins or
        benefits affected. If you believe an action was taken in error, you can contact
        support to have it reviewed. We will not treat programme balances as forfeit without
        a fair review process.
      </p>

      <h2 id="changes">15. Programme changes and closure</h2>
      <p>
        We may update, pause or close the programme. Where a change is materially adverse to
        customers, we will give reasonable prior notice by email or in-account notice where
        practicable. Immediate changes may be made where required for security, legal
        compliance, fraud prevention, or force-majeure reasons. These Programme Terms do not
        limit any statutory rights you have under Indian consumer law.
      </p>

      <h2 id="privacy">16. Privacy and communications</h2>
      <p>
        To run the programme, we use your account information, order and payment-status data,
        referral relationships, TruCoins ledger entries, and basic security and device
        signals, to operate the programme, prevent fraud, provide customer support, and
        send transactional notices (for example, coin credited, coin about to expire, order
        delivered). See our <Link to="/policies/privacy-policy">Privacy Policy</Link> for
        details. Marketing opt-in is <strong>separate and optional</strong>, is never
        pre-ticked, and is not required to participate in the programme.
      </p>

      <h2 id="grievance">17. Grievance, governing law and consumer rights</h2>
      <p>
        For any programme concern, contact us at{" "}
        <a href="mailto:hello@truwe.in">hello@truwe.in</a>. Our existing grievance,
        governing-law and jurisdiction clauses in our{" "}
        <Link to="/policies/terms-of-service">Terms of Service</Link> apply to these
        Programme Terms. Nothing in these Programme Terms overrides or limits any right you
        have under Indian consumer law.
      </p>

    </PolicyLayout>
  );
}
